import CollectionPlayer from "@/employee/CollectionPlayer"
import util from "@/util"
import { list, map, object, primitive, serializable } from "serializr"
import ValueMod from "@/effects/ValueMod"

export type MapStringAny = { [index: string]: any }

export default class ValueRange {
  @serializable
  private _baseValue: number = 0
  @serializable
  private readonly _min: number = 0
  @serializable
  private readonly _max: number = 0

  @serializable(map(object(ValueMod)))
  public valueMods: MapStringAny = {}

  @serializable(map(object(ValueMod)))
  public minMods: MapStringAny = {}

  @serializable(map(object(ValueMod)))
  public maxMods: MapStringAny = {}

  @serializable(list(primitive()))
  private readonly texts: string[] = []

  @serializable
  public readonly capBaseValue: boolean = false // If true, setting baseValue outside min/max range will not work

  constructor(value: number, min: number, max: number | undefined = undefined, text: string[] = [], capBaseValue = false) {
    this._baseValue = value
    this._min = min
    this._max = (max == undefined) ? Number.MAX_SAFE_INTEGER : max
    this.texts = text
    this.capBaseValue = capBaseValue
  }

  /**
   * Adds the given amount to the current final value.
   * @param value
   * @return how much the value was changed
   */
  addValue(value: number): number {
    let mulMods = []
    for (let key in this.valueMods) {
      let mod = this.valueMods[key]
      if (mod.type == 'mul') mulMods.push(mod)
    }
    let oldValue = this.value
    let oldBase = this.baseValue
    this.baseValue += ValueMod.reverseActivateAll(value, mulMods)
    return this.value - oldValue
  }

  /**
   * Adds the given amount to the current base value.
   * @param value
   * @return how much the base value was actually changed
   */
  addBaseValue(value: number): number {
    let oldBase = this.baseValue
    this.baseValue += value

    return this.baseValue - oldBase
  }

  set fraction(fraction: number) {
    this.baseValue = this.min + this.max * fraction
  }

  get fraction(): number {
    return (this.value - this.min) / (this.max - this.min)
  }

  get value(): number {
    let sum = ValueMod.activateAll(this._baseValue, Object.values(this.valueMods))

    if (sum > this.max) {
      return this.max
    } else if (sum < this.min) {
      return this.min
    } else {
      return sum
    }
  }

  set baseValue(value: number) {
    if (this.capBaseValue) {
      value = Math.max(Math.min(value, this.max), this.min)
    }
    this._baseValue = value
  }

  get baseValue(): number {
    return this._baseValue
  }

  get min(): number {
    return ValueMod.activateAll(this._min, Object.values(this.minMods))
  }

  //set min(value: number) {
  //  this._min = value;
  //}

  get max(): number {
    return ValueMod.activateAll(this._max, Object.values(this.maxMods))
  }

  //set max(value: number) {
  //  this._max = value;
  //}

  get text(): string {
    if (this.texts.length == 0) return this.value.toFixed(1)
    return util.pickFrom(this.texts, this.fraction)
  }

  load(state: ValueRange, loadBaseValue: boolean = true) {
    //this._min = state._min
    //this._max = state._max
    if (loadBaseValue) this._baseValue = state._baseValue
    this.valueMods = state.valueMods
    this.maxMods = state.maxMods
    this.minMods = state.minMods
  }

  get copy(): ValueRange {
    let v = new ValueRange(this._baseValue, this._min, this._max, this.texts)
    v.valueMods = this.valueMods
    return v
  }
}
